import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import MarkdownViewer from "../AIReviewDetailTemplate/ReviewSection/MarkdownViewer";
import { Button } from "components/atoms";
import { Spinner } from "components/atoms";
import { SlideManager } from "components/organisms/CommonUI/SlidePopup/SlideManager";
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";
import { ConfirmManager } from "components/organisms/CommonUI/ConfirmPopup/ConfirmManager";
import { useNavigationBlocker } from "hooks/useNavigationBlocker";
import { buildHospitalAliasMapFromGroups } from "utils/extractDepartment";
import { calculateAge } from "utils/TimeUtil";
import { formatGenderShort } from "utils/gender";
import { selectDrpSource, drpSourceOf } from "utils/selectDrpSource";
import { extractDrpChecklist, DrpChecklistResult } from "utils/extractDrpChecklist";
import { parseEditedHtmlToRoot } from "utils/drpSourceDom";
import {
  diffDrpChecklist,
  DrpDiffResult,
  MergedDrpRow,
  MergedEducationRow,
} from "utils/diffDrpChecklist";
import { nextGuidance, toDisplayDrpRow, toDisplayEducationRow } from "utils/checklistGuidance";
import type {
  AIReview,
  ChecklistGuidance,
  DrpSource,
  DrpFormat,
  Patient,
  TextConsultationContext,
} from "common/models/types";
import type {
  CreateTextConsultationBody,
  UpdateTextConsultationBody,
  CreateTextConsultationData,
} from "api/consultationService";
import {
  kstToday,
  isFutureDate,
  toDrpRequestRow,
  toEducationRequestRow,
} from "./buildTextRecordBody";
import {
  ensureEntry,
  clearUiCallbacks,
  enqueueSave,
  hasUnsavedChange,
  markTextRecordOpen,
  markTextRecordClosing,
  textRecordEnvKey,
  SaveStatus,
  TextRecordPayload,
} from "./textRecordSaveQueue";
import * as S from "./style";

interface ApiResult<T> {
  error: number;
  data?: T;
  message?: string;
  status?: number;
}

/** 머리 줄 환자 맥락에 쓰는 환자 필드(사이드바 `PatientSidebar`와 같은 필드 — 결정 20). */
export type TextRecordPatient = Pick<Patient, "birthDate" | "gender" | "memo">;

/**
 * 머리 줄 환자 맥락 `환자 {id} · {나이}세 {성별} · 메모 {memo}`(결정 20). 값이 없는 조각은 생략한다.
 * 성별은 저장값 표기가 유입 경로마다 달라(M·남·남성) 짧은 표기로 정규화한다(`utils/gender`).
 */
export function patientContextText(patientId: number, patient?: TextRecordPatient | null): string {
  const parts = [`환자 ${patientId}`];
  const age = patient?.birthDate ? calculateAge(patient.birthDate) : null;
  const ageGender = [
    age != null && Number.isFinite(age) ? `${age}세` : "",
    formatGenderShort(patient?.gender, ""),
  ]
    .filter(Boolean)
    .join(" ");
  if (ageGender) parts.push(ageGender);
  const memo = patient?.memo?.replace(/\s+/g, " ").trim();
  if (memo) parts.push(`메모 ${memo}`);
  return parts.join(" · ");
}

export interface TextRecordSlideProps {
  /** 고유 key(= 저장 큐 key). text-record:${recordId??"new"}:${reviewId??"none"}:${openSeq} */
  slideKey: string;
  patientId: number;
  reviewId: number | null;
  /** 기존 기록 id(수정 모드면 첫 저장부터 PATCH). 새 기록이면 null. */
  recordId?: number | null;
  /** 추출·alias 맵·수정 띠 판정용 보고서(상세). 못 받으면 null. */
  review?: AIReview | null;
  /** 기존 기록을 열 때 저장본 context(새 기록이면 null). */
  initialContext?: TextConsultationContext | null;
  /** 입력(edit) / 읽기(read). 기본 edit. */
  mode?: "edit" | "read";
  /** 머리 줄 환자 맥락(나이·성별·메모). 없으면 `환자 {id}`만. */
  patient?: TextRecordPatient | null;
  /** Page 콜백(API 호출은 Page만). */
  onCreate: (body: CreateTextConsultationBody) => Promise<ApiResult<CreateTextConsultationData>>;
  onUpdate: (id: number, body: UpdateTextConsultationBody) => Promise<ApiResult<void>>;
  /** 첫 생성 성공 때 1회(목록 재조회 + touch). */
  onCreated?: (recordId: number) => void;
  /** 닫힘 때(재조회). */
  onClose?: () => void;
  /** 상담일이 바뀔 때(목록 재조회 — 설계 결정 16). */
  onConsultedAtChanged?: () => void;
  /** 읽기 화면 「수정」 — Page가 입력 옵션으로 다시 show()(설계 결정 7). */
  onEdit?: (overrideContext?: TextConsultationContext) => void;
  /** 읽기 화면 「삭제」 — Page가 확인창 + soft delete. */
  onDelete?: () => void;
  /**
   * 읽기에서 연 수정 화면의 「닫기」 — 대기 변경을 보낸 뒤 읽기 화면으로 돌아간다(갈림길 표).
   * 주어지면 「닫기」가 전체 닫기 대신 Page에 현재 context를 넘겨 읽기 화면을 다시 show()하게 한다.
   * `discardFailed`가 true면(저장 실패한 대기 변경을 버리고 닫음) 현재 입력이 아니라
   * 서버 저장본으로 읽기 화면을 그려야 한다(결함 1).
   */
  onReturnToRead?: (currentContext: TextConsultationContext, discardFailed: boolean) => void;
}

/** 화면 상태 — 행은 `guidance`만 두고 `checked`는 직렬화 때 파생한다(설계 결정 21). */
type DisplayChecklist = {
  drpFormat: DrpFormat;
  drpItems: MergedDrpRow[];
  educationItems: MergedEducationRow[];
};

/** 저장본 context(상세·existing·읽기↔수정 전환) → 화면 상태. 표시는 `guidanceOf`로 읽고, 전환 때 넘어온 "새 항목"은 보존한다. */
function toDisplayChecklist(ctx: TextConsultationContext): DisplayChecklist {
  return {
    drpFormat: ctx.drpFormat,
    drpItems: ctx.drpItems.map((r) => toDisplayDrpRow(r, (r as MergedDrpRow).isNew)),
    educationItems: ctx.educationItems.map((r) => toDisplayEducationRow(r, (r as MergedEducationRow).isNew)),
  };
}

/** 읽기 셀 `aria-label`(세 상태 — 설계 결정 21). */
const GUIDANCE_READ_LABEL: Record<ChecklistGuidance | "none", string> = {
  done: "복약지도함",
  not_needed: "복약지도 안 함",
  none: "판단 안 함",
};

/** 표 위 안내(DRP·교육 표 공통 — 설계 결정 21). */
/** O·X 설명 블록(입력 모드, DRP 표 앞에 한 번) — 사용자 문구 결정 2026-10-14. */
const LEGEND_LEAD = "복약지도한 항목은 O, 하지 않은 항목은 X를 눌러 주세요.";
const LEGEND_SUB = "필요 없었거나 AI 판단이 틀린 항목도 X예요. 아직 판단 전이면 비워 두세요.";
const NOTE_PLACEHOLDER = "AI가 놓친 문제나 특이사항이 있다면 적어 주세요. X로 표시한 항목은 그 이유도 함께 남겨 주세요.";
const PURPOSE_TEXT = "남겨 주신 O·X와 메모는 PhAI가 더 나은 검토안을 만드는 데 쓰입니다";

const SAVE_STATUS_TEXT: Record<SaveStatus, string> = {
  idle: "",
  saving: "저장 중…",
  saved: "저장됨 · 방금",
  failed: "저장 실패 — 다시 시도",
  empty: "비어 있음",
};

/** 원본(MD) 경로용 숨김 렌더 → 추출 1회(보고서 id당 한 번, 설계 결정 4). */
const HiddenDrpExtractor: React.FC<{
  md: string;
  promptType?: string;
  aliasMap: ReturnType<typeof buildHospitalAliasMapFromGroups>;
  onExtract: (r: DrpChecklistResult) => void;
}> = ({ md, promptType, aliasMap, onExtract }) => {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (ref.current) onExtract(extractDrpChecklist(ref.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div ref={ref} style={{ display: "none" }} aria-hidden>
      <MarkdownViewer
        content={md}
        promptType={promptType}
        referenceMode="button"
        hospitalAliasMap={aliasMap}
      />
    </div>
  );
};

const TextRecordSlide: React.FC<TextRecordSlideProps> = ({
  slideKey,
  patientId,
  reviewId,
  recordId = null,
  review,
  initialContext,
  mode = "edit",
  patient,
  onCreate,
  onUpdate,
  onCreated,
  onClose,
  onConsultedAtChanged,
  onEdit,
  onDelete,
  onReturnToRead,
}) => {
  const isRead = mode === "read";
  const isExisting = !!initialContext;
  // 열림 판정 모드는 입력(edit)/읽기(read) 둘뿐 — "new"와 "edit"를 나누지 않는다. 첫 저장으로
  // recordId가 new→id로 바뀐 뒤에도 같은 열림으로 판정해야 재클릭이 무시된다(결함 9, 설계 결정 7).
  const env = useMemo(
    () => textRecordEnvKey(patientId, reviewId, isRead ? "read" : "edit"),
    [patientId, reviewId, isRead]
  );
  const selection = useMemo(
    () =>
      review ? selectDrpSource(review) : ({ kind: "none" } as ReturnType<typeof selectDrpSource>),
    [review]
  );
  const drpSource: DrpSource = review
    ? drpSourceOf(selection)
    : reviewId == null
      ? "none"
      : "original";
  const aliasMap = useMemo(
    () => buildHospitalAliasMapFromGroups(review?.patientSnap?.prescriptionGroups),
    [review]
  );

  // ── 추출(원천별) ───────────────────────────────────────────────────────
  const [extract, setExtract] = useState<{ result: DrpChecklistResult | null; loading: boolean }>(
    () => {
      if (!review || selection.kind === "none") {
        return { result: { drpFormat: "none", drpItems: [], educationItems: [] }, loading: false };
      }
      if (selection.kind === "edited") {
        return {
          result: extractDrpChecklist(parseEditedHtmlToRoot(selection.html)),
          loading: false,
        };
      }
      return { result: null, loading: true }; // original → 숨김 렌더
    }
  );

  // ── 편집 상태 ──────────────────────────────────────────────────────────
  const [checklist, setChecklist] = useState<DisplayChecklist>(() =>
    isExisting && initialContext
      ? toDisplayChecklist(initialContext)
      : { drpFormat: "none", drpItems: [], educationItems: [] }
  );
  const [note, setNote] = useState(initialContext?.note ?? "");
  const [consultedAt, setConsultedAt] = useState(initialContext?.consultedAt ?? kstToday());
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [banner, setBanner] = useState<DrpDiffResult | null>(null);
  const [dirty, setDirty] = useState(false);
  const reviewDate =
    initialContext?.reviewDate ??
    (review?.reviewDate ? String(review.reviewDate).slice(0, 10) : null);

  // refs(unmount cleanup이 stale closure 없이 읽도록 — 설계 결정 7)
  const checklistRef = useRef(checklist);
  const noteRef = useRef(note);
  const consultedAtRef = useRef(consultedAt);
  const saveStatusRef = useRef(saveStatus);
  const dirtyRef = useRef(false);
  const debounceRef = useRef<number | null>(null);
  // 상담일 키보드 입력의 중간 완전-날짜 change가 매번 저장되지 않도록 모아 보낸다(결함 6).
  const dateDebounceRef = useRef<number | null>(null);
  const seededRef = useRef(false);
  checklistRef.current = checklist;
  noteRef.current = note;
  consultedAtRef.current = consultedAt;
  saveStatusRef.current = saveStatus;

  const maxToday = kstToday();
  // 값이 오늘(KST)이면 "오늘" 태그(결정 9 · 화면 설계 3-1). 과거 날짜 안내 줄은 두지 않는다(사용자 지시 2026-10-14).
  const isToday = consultedAt === maxToday;

  // 투영 두 곳(buildPayload·buildCurrentContext)은 행을 명시 나열하는 toDrpRequestRow·toEducationRequestRow로만
  // 만든다 — 스프레드로 화면 전용 isNew가 body에 섞이면 400(설계 결정 21).
  const buildPayload = (): TextRecordPayload => ({
    patientId,
    reviewId,
    drpSource,
    drpFormat: checklistRef.current.drpFormat,
    drpItems: checklistRef.current.drpItems.map(toDrpRequestRow),
    educationItems: checklistRef.current.educationItems.map(toEducationRequestRow),
    note: noteRef.current,
    consultedAt: consultedAtRef.current,
  });

  /** 현재 편집 상태를 저장본 모양의 context로(읽기 화면 복귀·수정 전환용). */
  const buildCurrentContext = (): TextConsultationContext => ({
    source: "text",
    version: 2,
    consultedAt: consultedAtRef.current,
    reviewId,
    reviewDate,
    drpSource,
    drpFormat: checklistRef.current.drpFormat,
    drpItems: checklistRef.current.drpItems.map(toDrpRequestRow),
    educationItems: checklistRef.current.educationItems.map(toEducationRequestRow),
    note: noteRef.current,
  });

  // ── 큐 등록(콜백을 매 렌더 갱신 — stale 방지). deps 배열 없이 매 렌더 실행이 의도. ──────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    ensureEntry(
      slideKey,
      recordId,
      { onCreate, onUpdate, onCreated },
      {
        onStatus: setSaveStatus,
        onError: handleSaveError,
        onExisting: handleExisting,
      }
    );
  });

  // mount/unmount: 열림 등록 + cleanup flush(설계 결정 7 — ref로 대기 변경 읽음)
  useEffect(() => {
    markTextRecordOpen(env);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      if (dateDebounceRef.current) window.clearTimeout(dateDebounceRef.current);
      if (dirtyRef.current) {
        enqueueSave(slideKey, buildPayload());
        dirtyRef.current = false;
      }
      clearUiCallbacks(slideKey);
      markTextRecordClosing(env);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 라우트 변경 시 스스로 flush + hide(설계 결정 7)
  const location = useLocation();
  const initialPathRef = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== initialPathRef.current) {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      if (dateDebounceRef.current) window.clearTimeout(dateDebounceRef.current);
      if (dirtyRef.current) {
        enqueueSave(slideKey, buildPayload());
        dirtyRef.current = false;
      }
      SlideManager.hide();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // 새 기록: 추출 완료 시 체크 표를 seed(모든 행 빈 칸).
  useEffect(() => {
    if (isExisting || seededRef.current || !extract.result) return;
    seededRef.current = true;
    const next: DisplayChecklist = {
      drpFormat: extract.result.drpFormat,
      drpItems: extract.result.drpItems.map((r) => toDisplayDrpRow(r)),
      educationItems: extract.result.educationItems.map((r) => toDisplayEducationRow(r)),
    };
    setChecklist(next);
    checklistRef.current = next;
  }, [extract.result, isExisting]);

  // 기존 기록: 추출 완료 시 보고서 수정 띠 판정(설계 결정 19).
  useEffect(() => {
    if (!isExisting || !initialContext || !reviewId || !extract.result) return;
    if (extract.result.drpFormat === "none") {
      setBanner(null);
      return;
    }
    const d = diffDrpChecklist(
      {
        drpFormat: initialContext.drpFormat,
        drpItems: initialContext.drpItems,
        educationItems: initialContext.educationItems,
      },
      extract.result
    );
    setBanner(d.changed ? d : null);
  }, [extract.result, isExisting, reviewId, initialContext]);

  // 대기 변경(디바운스)·실패 시 브라우저 기본 확인창(설계 결정 7)
  useNavigationBlocker({ when: dirty || saveStatus === "failed" });

  // ── 저장 트리거 ─────────────────────────────────────────────────────────
  function handleSaveError(kind: "400" | "404" | "network", message: string) {
    ToastManager.show({ content: message, duration: 3000 });
    if (kind === "404") {
      // 404(생성=환자 없음 / 수정=기록 지워짐) — 재시도·확인창 없이 완전히 닫는다(결함 3).
      // onReturnToRead(읽기 복귀)로 가지 않는다: 기록이 없어 돌아갈 읽기 화면이 없다.
      // onClose가 환자 화면에서 목록을 재조회한다(설계 결정 16·18, 화면 설계 4 권한 없음).
      onClose?.();
      SlideManager.hide();
    }
  }

  function handleExisting(savedContext: TextConsultationContext, _recordId: number) {
    // 다른 탭이 만든 기록 — 저장본을 화면에 채우고 입력 중이던 글을 끝에 이어 붙인 뒤 수정 모드로(AC 461).
    const mergedNote = savedContext.note
      ? `${savedContext.note}${noteRef.current ? `\n${noteRef.current}` : ""}`
      : noteRef.current;
    const next = toDisplayChecklist(savedContext);
    setChecklist(next);
    checklistRef.current = next;
    setNote(mergedNote);
    noteRef.current = mergedNote;
    // 병합본을 다시 큐에 넣어 PATCH 1회로 남긴다 — 넣지 않으면 예약된 PATCH가 병합 전 상태를 보내
    // 저장본의 O·X·메모가 서버에서 지워진다(설계 결정 21 결함 ①).
    enqueueSave(slideKey, buildPayload());
    ToastManager.show({ content: "이미 있는 기록을 열었어요.", duration: 2500 });
  }

  /** O·X 버튼 — 하나만 켜지고 켜진 것을 다시 누르면 빈 칸(설계 결정 21). 누르는 즉시 저장(설계 결정 7). */
  const setRowGuidance = (kind: "drp" | "education", index: number, pressed: ChecklistGuidance) => {
    const apply = <T extends { guidance?: ChecklistGuidance }>(r: T): T => {
      const g = nextGuidance(r.guidance, pressed);
      const { guidance: _old, ...rest } = r;
      return (g ? { ...rest, guidance: g } : rest) as T;
    };
    const next: DisplayChecklist = {
      ...checklistRef.current,
      drpItems: checklistRef.current.drpItems.map((r, i) =>
        kind === "drp" && i === index ? apply(r) : r
      ),
      educationItems: checklistRef.current.educationItems.map((r, i) =>
        kind === "education" && i === index ? apply(r) : r
      ),
    };
    setChecklist(next);
    checklistRef.current = next;
    // atoms/Button은 mousedown에서 포커스를 옮기지 않아 메모 칸 blur가 일어나지 않는다. 이 저장이 입력 중인
    // 메모를 함께 실으므로 메모 디바운스를 거둔다(3초 뒤 같은 내용을 또 보내지 않게). 상담일 확정이 대기 중이면
    // 그쪽(목록 재조회 포함)이 dirty를 소비하도록 남긴다.
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (dirtyRef.current && !dateDebounceRef.current) {
      dirtyRef.current = false;
      setDirty(false);
    }
    enqueueSave(slideKey, buildPayload());
  };

  const scheduleDebounce = () => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      if (!dirtyRef.current) return; // 상담일 확정 등 다른 경로가 이미 flush했으면 중복 저장 안 함
      dirtyRef.current = false;
      setDirty(false);
      enqueueSave(slideKey, buildPayload());
    }, 3000);
  };

  const onNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let v = e.target.value;
    const cps = Array.from(v);
    if (cps.length > 10000) v = cps.slice(0, 10000).join("");

    // 결정 9(개정): 메모에 날짜 줄을 자동으로 넣지 않는다. 친 글자는 커서 자리에 그대로 남는다.
    setNote(v);
    noteRef.current = v;
    dirtyRef.current = true;
    setDirty(true);
    scheduleDebounce();
  };

  const onNoteBlur = () => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (dirtyRef.current) {
      dirtyRef.current = false;
      setDirty(false);
      enqueueSave(slideKey, buildPayload());
    }
  };

  // 상담일 저장을 확정 시 1회만 보낸다(결함 6). 키보드로 날짜를 치면 네이티브 date 입력이
  // 완전-유효 날짜 change를 여러 번 던지므로, 모아 두었다가 blur·확정(디바운스)에서 한 번 보낸다.
  const commitDate = () => {
    if (dateDebounceRef.current) {
      window.clearTimeout(dateDebounceRef.current);
      dateDebounceRef.current = null;
    }
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    setDirty(false);
    enqueueSave(slideKey, buildPayload());
    onConsultedAtChanged?.(); // 상담일 변경 → 목록 재조회(설계 결정 16)
  };

  const onDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    // 빈 값·미래는 상태도 바꾸지 않는다(중간 상태·미래 차단은 유지).
    if (!v || isFutureDate(v, maxToday)) return;
    setConsultedAt(v);
    consultedAtRef.current = v;
    dirtyRef.current = true;
    setDirty(true);
    if (dateDebounceRef.current) window.clearTimeout(dateDebounceRef.current);
    dateDebounceRef.current = window.setTimeout(commitDate, 500);
  };

  const onDateBlur = () => commitDate();

  const onApplyChangedTable = () => {
    if (!banner || !extract.result) return;
    // 누르는 시점의 화면 표시(checklistRef.current)로 다시 맞춘다 — 열 때 판정한 병합본(저장본 기준)을 쓰면
    // 띠가 뜬 동안 바꾼 O·X가 되돌아간다(설계 결정 21 결함 ②).
    const merged = diffDrpChecklist(checklistRef.current, extract.result);
    const next: DisplayChecklist = {
      drpFormat: extract.result.drpFormat,
      drpItems: merged.mergedDrpItems,
      educationItems: merged.mergedEducationItems,
    };
    setChecklist(next);
    checklistRef.current = next;
    setBanner(null);
    enqueueSave(slideKey, buildPayload());
    // 읽기 화면에서 눌렀으면 수정 화면으로 전환(AC 481) — 병합본을 넘겨 다시 show().
    // "새 항목"(isNew)은 재열기까지 보존해야 배지가 보인다(결함 5). 요청 body는 명시 투영이라 isNew가 섞이지 않는다.
    if (isRead) {
      const ctx = buildCurrentContext();
      onEdit?.({
        ...ctx,
        drpItems: ctx.drpItems.map((r, i) => (next.drpItems[i].isNew ? { ...r, isNew: true } : r)),
        educationItems: ctx.educationItems.map((r, i) =>
          next.educationItems[i].isNew ? { ...r, isNew: true } : r
        ),
      });
    }
  };

  const doClose = () => {
    // 읽기에서 연 수정 화면이면 닫기 = 읽기 화면으로 복귀(Page가 현재 context로 다시 show).
    if (onReturnToRead) {
      // 저장 실패한 대기 변경을 버리고 닫을 때는 현재 입력이 아니라 서버 저장본으로 그린다(결함 1).
      const discardFailed = saveStatusRef.current === "failed" && hasUnsavedChange(slideKey);
      onReturnToRead(buildCurrentContext(), discardFailed);
      return;
    }
    onClose?.();
    SlideManager.hide();
  };

  const handleClose = () => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (dateDebounceRef.current) window.clearTimeout(dateDebounceRef.current);
    if (dirtyRef.current) {
      dirtyRef.current = false;
      setDirty(false);
      enqueueSave(slideKey, buildPayload());
    }
    if (saveStatusRef.current === "failed" && hasUnsavedChange(slideKey)) {
      ConfirmManager.show({
        title: "",
        body: "저장하지 못한 내용이 있어요. 그래도 닫을까요?",
        buttons: [
          <Button
            key="cancel"
            buttonType="button"
            color="Tertiary"
            size="Medium"
            type="Text"
            variant="Outlined"
            onClick={() => ConfirmManager.hide()}
          >
            취소
          </Button>,
          <Button
            key="close"
            buttonType="button"
            color="Primary"
            size="Medium"
            type="Text"
            variant="Contained"
            onClick={() => {
              ConfirmManager.hide();
              doClose();
            }}
          >
            닫기
          </Button>,
        ],
        showBackdrop: true,
        backdropClose: false,
        showCloseButton: false,
      });
      return;
    }
    doClose();
  };

  // ── 렌더 ────────────────────────────────────────────────────────────────
  // 표시 기준은 화면 체크리스트(기존 기록은 저장본, 새 기록은 추출). 추출은 띠 판정용.
  // 기존 기록(읽기·수정)은 저장본을 바로 보여 준다 — 추출은 띠 판정용이라 로딩을 기다리지 않는다.
  const sectionsReady = isExisting || (reviewId != null && !extract.loading);
  const showChecklistSections = sectionsReady && checklist.drpFormat !== "none";
  const showDrpUnavailable =
    reviewId != null && !extract.loading && checklist.drpFormat === "none" && !isExisting;

  // 행 클릭 토글·hover는 두지 않는다 — O·X 버튼(28px)을 정확히 눌러야 표시가 바뀐다(설계 결정 21, 사용자 결정 2026-10-14).
  const renderGuidanceCell = (
    guidance: ChecklistGuidance | undefined,
    kind: "drp" | "education",
    index: number
  ) =>
    isRead ? (
      <S.GuidanceMark
        data-testid="guidance-read"
        $guidance={guidance}
        aria-label={GUIDANCE_READ_LABEL[guidance ?? "none"]}
      >
        {guidance === "done" ? "O" : guidance === "not_needed" ? "X" : ""}
      </S.GuidanceMark>
    ) : (
      <S.GuidanceButtons>
        <Button
          buttonType="button"
          color={guidance === "done" ? "Primary" : "Tertiary"}
          size="Small"
          type="Text"
          variant={guidance === "done" ? "Contained" : "Outlined"}
          onClick={() => setRowGuidance(kind, index, "done")}
          aria-label="복약지도함"
          aria-pressed={guidance === "done"}
          data-testid="guidance-done"
        >
          O
        </Button>
        <Button
          buttonType="button"
          color={guidance === "not_needed" ? "Secondary" : "Tertiary"}
          size="Small"
          type="Text"
          variant={guidance === "not_needed" ? "Contained" : "Outlined"}
          onClick={() => setRowGuidance(kind, index, "not_needed")}
          aria-label="복약지도 안 함"
          aria-pressed={guidance === "not_needed"}
          data-testid="guidance-not-needed"
        >
          X
        </Button>
      </S.GuidanceButtons>
    );

  return (
    <S.SlideRoot>
      {selection.kind === "original" && extract.loading && (
        <HiddenDrpExtractor
          md={(selection as { md: string }).md}
          promptType={review?.promptType}
          aliasMap={aliasMap}
          onExtract={(r) => setExtract({ result: r, loading: false })}
        />
      )}

      {/* 머리 줄 하나: 제목 · 환자 맥락 · 저장 상태 · 자체 닫기(화면 설계 3-1 · 결정 20). */}
      <S.HeaderRow1>
        <S.HeaderLeft>
          <S.HeaderTitle data-testid="checklist-title">상담 체크리스트</S.HeaderTitle>
          <S.PatientText data-testid="patient-field" title={patientContextText(patientId, patient)}>
            {patientContextText(patientId, patient)}
          </S.PatientText>
          {!isRead && (
            <S.SaveStatusText $failed={saveStatus === "failed"}>
              {saveStatus === "failed" ? (
                <>
                  저장 실패 —{" "}
                  <Button
                    buttonType="button"
                    color="Primary"
                    size="Small"
                    type="Text"
                    variant="Ghost"
                    onClick={() => enqueueSave(slideKey, buildPayload())}
                  >
                    다시 시도
                  </Button>
                </>
              ) : (
                SAVE_STATUS_TEXT[saveStatus]
              )}
            </S.SaveStatusText>
          )}
        </S.HeaderLeft>
        <S.CloseCircle>
          <Button
            buttonType="button"
            color="Tertiary"
            size="Medium"
            type="Icon"
            variant="Ghost"
            icon="X"
            onClick={handleClose}
            aria-label="닫기"
          />
        </S.CloseCircle>
      </S.HeaderRow1>

      {/* 머리 줄 아래·라벨 칸 위 목적 한 줄(결정 20). */}

      {/* 라벨 칸 두 개는 머리 줄의 하위 — 서로는 붙이고(6px) 머리 줄과는 띄운다(16px + 구분선). */}
      <S.FieldGroup>
        {/* 라벨 칸 1 「복약지도한 날」: 오늘이면 태그, 아니면 칸 아래 한 줄(결정 9·20 · 화면 설계 3-1). */}
        <div>
          <S.LabelRow>
            <S.FieldLabel>복약지도한 날</S.FieldLabel>
            <S.FieldValue>
              {isRead ? (
                <S.DateStatic aria-label="복약지도한 날">{consultedAt}</S.DateStatic>
              ) : (
                <S.DateInput
                  type="date"
                  aria-label="복약지도한 날"
                  value={consultedAt}
                  max={maxToday}
                  onChange={onDateChange}
                  onBlur={onDateBlur}
                />
              )}
              {isToday && <S.TodayTag data-testid="today-tag">오늘</S.TodayTag>}
            </S.FieldValue>
          </S.LabelRow>
        </div>

        {/* 라벨 칸 2 「보고서」: 글자 표기(입력·읽기 동일 — 화면 설계 3-1, 보고서 선택은 결정 20으로 폐기). */}
        <div>
          <S.LabelRow>
            <S.FieldLabel>보고서</S.FieldLabel>
            <S.FieldValue data-testid="report-field">
              {reviewId != null ? (
                <span>
                  {`#${reviewId}${reviewDate ? ` · ${reviewDate} 작성` : ""}`}
                  {drpSource === "edited" && <S.DimTail> · 수정본 기준</S.DimTail>}
                </span>
              ) : (
                <span>없음 · 메모만</span>
              )}
            </S.FieldValue>
          </S.LabelRow>
        </div>
      </S.FieldGroup>

      {banner && (
        <S.Banner>
          <span>보고서가 수정되었어요. 문구가 바뀐 항목은 다시 확인해 주세요.</span>
          <Button
            buttonType="button"
            color="Primary"
            size="Small"
            type="Text"
            variant="Outlined"
            onClick={onApplyChangedTable}
          >
            바뀐 표 가져오기
          </Button>
        </S.Banner>
      )}

      {extract.loading && !isExisting && (
        <S.SectionBlock>
          <Spinner size="Medium" />
        </S.SectionBlock>
      )}

      {showDrpUnavailable && (
        <S.EmptyNotice>
          이 보고서에서 DRP를 불러오지 못했어요. 메모만 적을 수 있어요.
        </S.EmptyNotice>
      )}

      {showChecklistSections && !isRead && (
        <S.Legend data-testid="guidance-legend">
          <S.LegendLead>{LEGEND_LEAD}</S.LegendLead>
          <span>{LEGEND_SUB}</span>
        </S.Legend>
      )}

      {showChecklistSections && (
        <S.SectionBlock>
          <S.SectionLabel>DRP 체크</S.SectionLabel>
          <S.CheckTable data-testid="drp-check-table">
            <thead>
              <tr>
                <th>복약지도</th>
                <th>🚦</th>
                <th>유형</th>
                <th>문제 요약</th>
              </tr>
            </thead>
            <tbody>
              {checklist.drpItems.map((row, i) => (
                <S.CheckRow key={i} $guidance={row.guidance}>
                  <td>{renderGuidanceCell(row.guidance, "drp", i)}</td>
                  <td>{row.severity}</td>
                  <td>{row.type}</td>
                  <td>
                    {row.summary}
                    {row.isNew && <S.NewBadge>새 항목</S.NewBadge>}
                  </td>
                </S.CheckRow>
              ))}
            </tbody>
          </S.CheckTable>
        </S.SectionBlock>
      )}

      {showChecklistSections && checklist.educationItems.length > 0 && (
        <S.SectionBlock>
          <S.SectionLabel>약사 확인·교육 체크</S.SectionLabel>
          <S.CheckTable data-testid="education-check-table">
            <thead>
              <tr>
                <th>복약지도</th>
                <th>유형</th>
                <th>내용</th>
              </tr>
            </thead>
            <tbody>
              {checklist.educationItems.map((row, i) => (
                <S.CheckRow key={i} $guidance={row.guidance}>
                  <td>{renderGuidanceCell(row.guidance, "education", i)}</td>
                  <td>{row.type}</td>
                  <td>
                    {row.summary}
                    {row.isNew && <S.NewBadge>새 항목</S.NewBadge>}
                  </td>
                </S.CheckRow>
              ))}
            </tbody>
          </S.CheckTable>
        </S.SectionBlock>
      )}

      <S.SectionBlock>
        <S.SectionLabel>메모 (선택)</S.SectionLabel>
        {isRead ? (
          <S.NoteRead data-testid="note-read">{note}</S.NoteRead>
        ) : (
          <>
            <S.NoteArea
              value={note}
              maxLength={10000}
              placeholder={NOTE_PLACEHOLDER}
              onChange={onNoteChange}
              onBlur={onNoteBlur}
              aria-label="메모 (선택)"
            />
            <S.GuideText>환자 이름·연락처는 적지 말아 주세요</S.GuideText>
          </>
        )}
        <S.PurposeText data-testid="purpose-text">{PURPOSE_TEXT}</S.PurposeText>
      </S.SectionBlock>

      {isRead && (
        <S.FooterRow>
          <Button
            buttonType="button"
            color="Primary"
            size="Medium"
            type="Text"
            variant="Outlined"
            onClick={() => onEdit?.()}
          >
            수정
          </Button>
          <Button
            buttonType="button"
            color="Negative"
            size="Medium"
            type="Text"
            variant="Outlined"
            onClick={() => onDelete?.()}
          >
            삭제
          </Button>
        </S.FooterRow>
      )}
    </S.SlideRoot>
  );
};

export default TextRecordSlide;
